import { z } from "zod";

import { isValidCivilDate } from "./civil-date";
import {
  isSensitiveQuery,
  normalizeQuery,
  unicodeLength,
} from "./query-normalizer";
import type {
  PublicSearchRequest,
  SearchCriteria,
  SearchMode,
} from "./public-search-types";

const ALLOWED_REQUEST_FIELDS = new Set(["query", "eventDateFrom", "eventLocation", "page"]);

const rawPublicSearchRequestSchema = z
  .object({
    query: z.string().optional(),
    eventDateFrom: z.string().optional(),
    eventLocation: z.string().optional(),
    page: z.number().int("A página deve ser um número inteiro.").min(1, "A página deve ser maior ou igual a 1."),
  })
  .passthrough()
  .superRefine((request, context) => {
    if (Object.keys(request).some((key) => !ALLOWED_REQUEST_FIELDS.has(key))) {
      context.addIssue({
        code: "custom",
        message: "A requisição contém campos não permitidos.",
      });
    }
  });

export const publicSearchRequestSchema = rawPublicSearchRequestSchema
  .transform(({ query, eventDateFrom, eventLocation, page }) => {
    const normalizedQuery = query === undefined ? undefined : normalizeQuery(query);
    // The place goes through the same normaliser: trimmed and collapsed, so a stray space does
    // not become a criterion of its own.
    const normalizedLocation = eventLocation === undefined ? undefined : normalizeQuery(eventLocation);

    return {
      ...(normalizedQuery === undefined || normalizedQuery.length === 0
        ? {}
        : { query: normalizedQuery }),
      ...(eventDateFrom === undefined ? {} : { eventDateFrom }),
      ...(normalizedLocation === undefined || normalizedLocation.length === 0
        ? {}
        : { eventLocation: normalizedLocation }),
      page,
    };
  })
  .superRefine((request, context) => {
    if (request.query !== undefined) {
      if (unicodeLength(request.query) > 120) {
        context.addIssue({
          code: "custom",
          path: ["query"],
          message: "A busca deve ter no máximo 120 caracteres.",
        });
      } else if (isSensitiveQuery(request.query)) {
        context.addIssue({
          code: "custom",
          path: ["query"],
          message: "Consulta não permitida.",
        });
      }
    }

    if (
      request.eventDateFrom !== undefined &&
      !isValidCivilDate(request.eventDateFrom)
    ) {
      context.addIssue({
        code: "custom",
        path: ["eventDateFrom"],
        message: "Informe uma data válida no formato AAAA-MM-DD.",
      });
    }

    if (request.eventLocation !== undefined && unicodeLength(request.eventLocation) > 255) {
      // The column's own ceiling (`gift_lists.event_location`, VARCHAR(255)).
      context.addIssue({
        code: "custom",
        path: ["eventLocation"],
        message: "A localidade deve ter no máximo 255 caracteres.",
      });
    }

    if (
      request.query === undefined &&
      request.eventDateFrom === undefined &&
      request.eventLocation === undefined
    ) {
      context.addIssue({
        code: "custom",
        path: ["query"],
        message: "Informe um nome, título, data ou localidade para buscar.",
      });
    }
  });

const publicGiftListItemSchema = z
  .object({
    id: z.string().refine((value) => value.trim().length > 0),
    title: z.string().refine((value) => value.trim().length > 0),
    personName1: z.string().nullable(),
    personName2: z.string().nullable(),
    eventDate: z.string().refine(isValidCivilDate).nullable(),
    eventLocation: z.string().nullable(),
    featuredImageUrl: z.string().url().nullable(),
  })
  .strict();

export const publicSearchPageSchema = z
  .object({
    items: z.array(publicGiftListItemSchema).max(12),
    page: z.number().int().min(1),
    pageSize: z.literal(12),
    totalItems: z.number().int().min(0),
    totalPages: z.number().int().min(0),
    hasNextPage: z.boolean(),
  })
  .strict();

export const publicSearchErrorResponseSchema = z
  .object({
    ok: z.literal(false),
    code: z.enum([
      "INVALID_INPUT",
      "FORBIDDEN",
      "RATE_LIMITED",
      "SERVER_ERROR",
    ]),
  })
  .strict();

export function classifySearchCriteria(criteria: SearchCriteria): SearchMode {
  const hasQuery = criteria.query !== undefined;
  const hasEventDate = criteria.eventDateFrom !== undefined;

  if (hasQuery && hasEventDate) return "combined";
  if (hasQuery) return "name";
  if (hasEventDate) return "date";

  throw new TypeError("Critérios de busca ausentes.");
}

export function parsePublicSearchRequest(input: unknown): PublicSearchRequest {
  return publicSearchRequestSchema.parse(input);
}
